import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { promoteProject, updateDraft } from '@/entities/project';
import { applyPrefill } from '@/features/create-project/model/applyPrefill';
import {
  buildCreateProjectSchema,
  defaultFormData,
  WIZARD_STEP_ARRAY,
  WIZARD_STEPS,
  type CreateProjectFormData,
} from '@/features/create-project/model/schema';
import { useProjectUploadStore } from '@/features/create-project/model/upload.store';
import { UploadCancelledError } from '@/features/create-project/model/upload.store.types';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { ROUTES } from '@/shared/config/routes';
import { track } from '@/shared/analytics';

export type UseCreateProjectWizardReturn = ReturnType<typeof useCreateProjectWizard>;

export function useCreateProjectWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('projects');
  const { t: tValidation } = useTranslation('validation');

  const files = useProjectUploadStore((s) => s.files);
  const uploadStatus = useProjectUploadStore((s) => s.uploadStatus);
  const startUploadFlow = useProjectUploadStore((s) => s.startUploadFlow);
  const ensureDraftProject = useProjectUploadStore((s) => s.ensureDraftProject);
  const analyzeDocuments = useProjectUploadStore((s) => s.analyzeDocuments);
  const finishUpload = useProjectUploadStore((s) => s.finishUpload);
  const cancelAnalysis = useProjectUploadStore((s) => s.cancelAnalysis);
  const projectId = useProjectUploadStore((s) => s.projectId);
  const reset = useProjectUploadStore((s) => s.reset);

  const formSchema = useMemo(() => buildCreateProjectSchema(tValidation), [tValidation]);

  const form = useForm<CreateProjectFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormData,
    mode: 'onChange',
  });

  useEffect(() => {
    return () => {
      cancelAnalysis();
      reset();
    };
  }, [cancelAnalysis, reset]);

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const activeStepId = WIZARD_STEP_ARRAY[activeStepIndex].id;
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === WIZARD_STEP_ARRAY.length - 1;

  const isUploading =
    uploadStatus === 'creating-project' ||
    uploadStatus === 'uploading' ||
    uploadStatus === 'analyzing';

  const onSubmit = useCallback(
    async (formData: CreateProjectFormData) => {
      if (!projectId) {
        logger.error(
          'onSubmit called without projectId — draft was not created before reaching step 2',
        );
        return;
      }

      try {
        // Draft phase — the project is addressed by its internal id until published.
        await updateDraft(projectId, {
          type: formData.type,
          title: formData.title,
          description: formData.description || undefined,
          tags: formData.tags ?? [],
          market: formData.markets.join(', ') || undefined,
          status: formData.status || undefined,
          startDate: formData.startDate || undefined,
          deadline: formData.deadline || undefined,
          audienceIds: formData.audienceIds?.length ? formData.audienceIds : undefined,
        });

        const published = await promoteProject(projectId);
        const platformProjectId = published.platformProjectId;
        if (!platformProjectId) {
          throw new Error('Published project is missing a platform project ID');
        }

        track('project_created', { project_id: platformProjectId });

        // Bust every paginated projects-list query so navigating back to
        // /projects picks up the newly-promoted row. Without this, the wizard
        // creates → publishes → navigates away, and the next visit to the
        // list renders from a stale TanStack cache that pre-dates the new
        // project.
        void queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });

        notificationService.showSuccess(
          t('createProject.useCreateProjectWizard.yourProjectHasBeenCreated'),
          {
            description: t('createProject.useCreateProjectWizard.youCanNowStartBriefing'),
            anchorOrigin: {
              horizontal: 'center',
              vertical: 'bottom',
            },
          },
        );

        void navigate(ROUTES.PROJECT_DETAIL(platformProjectId));
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        const message =
          status === 409
            ? t('createProject.useCreateProjectWizard.aProjectWithThisTitle')
            : t('createProject.useCreateProjectWizard.somethingWentWrongWhileCreating');
        notificationService.showError(message);
      }
    },
    [projectId, navigate, t],
  );

  const { trigger, handleSubmit } = form;

  const goNext = useCallback(async () => {
    const currentStep = WIZARD_STEP_ARRAY[activeStepIndex];
    const isValid = await trigger(currentStep.requiredFields, { shouldFocus: true });
    if (!isValid) return;

    if (isLastStep) {
      await handleSubmit(onSubmit)();
      return;
    }

    if (files.length > 0) {
      try {
        await startUploadFlow();
      } catch (error) {
        // Pure user-cancel surfaces as a typed sentinel. Catch silently —
        // no toast, no advance. The user pressed cancel; toasting
        // "canceled" and refusing to advance would be hostile feedback
        // for their own intentional action.
        // Their queue items are already 'cancelled' in the global queue.
        if (error instanceof UploadCancelledError) {
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : t('createProject.useCreateProjectWizard.failedToProceed');
        notificationService.showError(message);
        return;
      }

      // Best-effort document analysis. A user-cancelled ('aborted') or
      // timed-out/failed ('error') analysis must never block advancing to
      // the Details step — only a real failure surfaces a toast.
      const result = await analyzeDocuments();
      if (result.outcome === 'ok' && result.prefill) {
        applyPrefill(form, result.prefill);
      } else if (result.outcome === 'error') {
        notificationService.showError(
          t('createProject.useCreateProjectWizard.weCouldNotAnalyzeYour'),
        );
      }

      setActiveStepIndex((prev) => prev + 1);
      finishUpload();
      return;
    }

    setActiveStepIndex((prev) => prev + 1);
  }, [
    activeStepIndex,
    isLastStep,
    trigger,
    handleSubmit,
    onSubmit,
    files.length,
    startUploadFlow,
    analyzeDocuments,
    finishUpload,
    form,
    t,
  ]);

  const skip = useCallback(async () => {
    if (isLastStep) return;

    try {
      await ensureDraftProject();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('createProject.useCreateProjectWizard.failedToProceed');
      notificationService.showError(message);
      return;
    }
    setActiveStepIndex((prev) => prev + 1);
  }, [isLastStep, ensureDraftProject, t]);

  const cancel = useCallback(() => {
    reset();
    void navigate(-1);
  }, [navigate, reset]);

  const cancelOrAbort = useCallback(() => {
    if (uploadStatus === 'analyzing') {
      cancelAnalysis();
      return;
    }
    cancel();
  }, [uploadStatus, cancelAnalysis, cancel]);

  const hasFilesOnIntake = activeStepId === WIZARD_STEPS.intake.id && files.length > 0;
  const canContinue = !isUploading && (activeStepId !== WIZARD_STEPS.intake.id || hasFilesOnIntake);

  return {
    ...form,
    activeStepId,
    activeStepIndex,
    isFirstStep,
    isLastStep,
    isUploading,
    uploadStatus,
    canContinue,
    goNext,
    skip,
    cancel,
    cancelOrAbort,
  };
}
